import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { downloadFile, urlToBase64 } from '@/utils';
import {
  File,
  FileNameTypes,
  FileTypes,
  FileUploadStatus,
  ImageType,
  useDeleteFilesMutation,
  useUpdateFileStatusMutation,
} from '@/__generated__/graphql';
import { MediaViewer } from '@/components/shared/MediaViewer';
import { FileDisplay } from './FileDisplay';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useAssemblyAttachmentCaption } from '@/hooks/useAssemblyAttachmentCaption';
import { useSetAtom } from 'jotai';
import {
  sideBySideModeEnabled,
  selectedIsometricFileId,
  drawerLeft,
} from '@/components/Analysis/state';

type Props = {
  referenceId: string;
  collectionName: string;
  files: File[];
  attachments: Attachment[];
  setAttachments: React.Dispatch<React.SetStateAction<Attachment[]>>;
  enableUpload?: boolean;
  onUploadComplete?: (index: number, file: Attachment) => void;
  showRemoveIcon?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  handleRemoveAttachment?: (index: number) => void;
  allFiles: File[];
  location?: string;
};

export const ListFiles = ({
  referenceId,
  collectionName,
  files,
  attachments,
  setAttachments,
  setIsUpdating,
  allFiles,
  location = '',
}: Props) => {
  const [UpdateFileStatus] = useUpdateFileStatusMutation();
  const { updateAssemblyAttachmentCaption } = useAssemblyAttachmentCaption();
  const [deleteFiles] = useDeleteFilesMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const setSideBySideModeEnabled = useSetAtom(sideBySideModeEnabled);
  const setSelectedIsometricFileId = useSetAtom(selectedIsometricFileId);
  const setDrawerLeft = useSetAtom(drawerLeft);

  useEffect(() => {
    (async function getImageURLs() {
      if (files && files.length > 0) {
        const fetchedFiles = await Promise.all(
          files
            .filter(
              (file) =>
                file.uploadStatus &&
                [FileUploadStatus.Success, FileUploadStatus.Inprogress].includes(file.uploadStatus)
            )
            .map(
              async ({
                fileName,
                resourcePath,
                type,
                id,
                uploadStatus,
                caption,
                imageType,
                uuid,
                name,
              }) => {
                const fileUrl = resourcePath ? getCloudfrontUrl(resourcePath) : '';
                const isImage =
                  type.includes('image/') ||
                  [FileTypes.Jpeg, FileTypes.Png].includes(type as FileTypes);
                const base64 = isImage ? await urlToBase64(fileUrl) : undefined;
                return {
                  id: id || '',
                  name: fileName,
                  base64: base64,
                  type,
                  caption: caption || undefined,
                  isNew: false,
                  url: fileUrl,
                  uploadStatus: uploadStatus || FileUploadStatus.Success,
                  imageType: imageType || undefined,
                  uuid: uuid || undefined,
                  fileName,
                  disableFullScreen: name === FileNameTypes.IsometricDrawing,
                  fileNameType: name,
                };
              }
            )
        );
        setAttachments(() => fetchedFiles);
      }
    })();
  }, [files, setAttachments]);

  const onUploadComplete = (index: number, file: Attachment) => {
    if (file.isNew && file.resourcePath) {
      const poiAttachmentUrl = getCloudfrontUrl(file.resourcePath);
      setAttachments((previous) => {
        const newAttachments = [...previous];
        newAttachments[index] = {
          ...newAttachments[index],
          url: poiAttachmentUrl,
          isNew: false,
          uploadStatus: FileUploadStatus.Success,
        };
        return newAttachments;
      });
      if (setIsUpdating) setIsUpdating(false);
    }
  };

  const handleDownloadFile = (attachmentIndex: number) => {
    const currentAttachment = attachments[attachmentIndex];
    if (!currentAttachment.url) return;
    downloadFile(currentAttachment.url, currentAttachment.name);
  };

  const handleRemoveFile = (fileIndex: number) => {
    const currentFile = attachments.filter(
      (file) => !file.imageType || file.imageType !== ImageType.WhiteBgPdf
    )[fileIndex];
    if (!currentFile.id) return;
    const filesToRemove: string[] = [];
    filesToRemove.push(currentFile.id);
    const otherFilesToRemove = allFiles?.find(
      (eachFile) =>
        eachFile.id !== currentFile.id &&
        eachFile.uuid === `${currentFile.uuid}-${ImageType.WhiteBgPdf}` &&
        eachFile.imageType === ImageType.WhiteBgPdf
    );
    if (otherFilesToRemove?.id) filesToRemove.push(otherFilesToRemove.id);
    deleteFiles({
      variables: {
        input: {
          referenceId,
          collectionName,
          fileIds: filesToRemove,
        },
      },
    });
    setAttachments(attachments.filter((file) => file.id !== currentFile.id));
  };

  const handelAssemblyAttachmentUpload = async (fileId: string | undefined) => {
    if (!fileId) return;
    await UpdateFileStatus({
      variables: {
        input: {
          referenceId,
          collectionName,
          fileUpdates: [
            {
              uploadStatus: FileUploadStatus.Success,
              fileId,
            },
          ],
        },
      },
      onError: (error) => {
        showSnackBar(error.message, 'error');
      },
    });
  };

  const handelAssemblyImageCaption = async (updatedCaption: string, fileId: string | undefined) => {
    if (!fileId) return;
    await updateAssemblyAttachmentCaption(referenceId, fileId, updatedCaption);
  };

  const handleClick = (e: React.MouseEvent, attachment?: Attachment) => {
    if (!attachment?.id || attachment.fileNameType !== FileNameTypes.IsometricDrawing) return;
    setSelectedIsometricFileId(attachment.id);
    setSideBySideModeEnabled(true);
    setDrawerLeft({
      visibility: false,
      query: false,
    });
  };

  return (
    <Grid container spacing={{ xl: 3, md: 2 }} px={2} id={`${location}-attachments`}>
      <MediaViewer
        mediaFiles={attachments.filter(
          (file) => !file.imageType || file.imageType !== ImageType.WhiteBgPdf
        )}
        handleDownloadAttachment={handleDownloadFile}
        handleRemoveAttachment={handleRemoveFile}
      >
        {attachments.map((file, index) =>
          !file.imageType || file.imageType !== ImageType.WhiteBgPdf ? (
            <Grid item lg={4} sm={6} key={file.id || index}>
              <FileDisplay
                referenceId={referenceId}
                collectionName={collectionName}
                selectedAttachment={file}
                selectedAttachmentIndex={index}
                showRemoveIcon={false}
                enableUpload={true}
                onUploadComplete={onUploadComplete}
                uploadAttachmentHandler={() => handelAssemblyAttachmentUpload(file.id)}
                imageCaptureHandler={(caption) => handelAssemblyImageCaption(caption, file.id)}
                handleClick={handleClick}
                location={location}
              />
            </Grid>
          ) : (
            <></>
          )
        )}
      </MediaViewer>
    </Grid>
  );
};
